import { useCallback, useEffect, useRef } from 'react';

import { Coords, ElementSize, Page } from 'editor/src/store/design/types';
import { useSelector } from 'editor/src/store/hooks';

import useFabricCanvas from 'editor/src/util/useFabricCanvas';
import useFabricUtils from 'editor/src/util/useFabricUtils';

import { CanvasRotation, VIEWPORT_CHANGED_EVENT } from 'editor/src/component/EditorArea/types';

import { useIsMobile } from '../../useDetectDeviceType';
import useCanvasRotation from '../useCanvasRotation';

const PADDING = 20;

export type PlaceholderSizeVariant = 'extra-small' | 'small' | 'medium' | 'large';

export function getSizeVariant(visualSize: number): PlaceholderSizeVariant {
  if (visualSize <= 150) {
    return 'extra-small';
  }
  if (visualSize < 200) {
    return 'small';
  }
  if (visualSize < 300) {
    return 'medium';
  }
  return 'large';
}

function usePlaceholderPositioning(
  content: ElementSize,
  spreadCoords: Coords,
  canvasRotation: CanvasRotation,
  page: Page,
) {
  const placeholderContainerRef = useRef<HTMLDivElement>(null);
  const isDragging = useSelector((state) => state.editor.dragState !== undefined);
  const { mm2px } = useFabricUtils();
  const isMobile = useIsMobile();
  const fabricCanvas = useFabricCanvas();

  const contentWidth = mm2px(content.width);
  const contentHeight = mm2px(content.height);
  const contentX = spreadCoords.left + mm2px((page?.x ?? 0) + content.x + content.width / 2);
  const contentY = spreadCoords.top + mm2px((page?.y ?? 0) + content.y + content.height / 2);
  const rotatedPoint = useCanvasRotation(canvasRotation, contentX, contentY);

  const width = contentWidth - PADDING * 2;
  const height = contentHeight - PADDING * 2;
  const placeholderSize = Math.min(width, height);

  const getPosition = useCallback(() => {
    const offsetX = fabricCanvas.viewportTransform ? fabricCanvas?.viewportTransform[4] : 0;
    const offsetY = fabricCanvas.viewportTransform ? fabricCanvas?.viewportTransform[5] : 0;

    const zoom = fabricCanvas.getZoom();
    // TODO with the canvas rotation offset
    const left = (rotatedPoint.x - placeholderSize / 2) * zoom + offsetX;
    const top = (rotatedPoint.y - placeholderSize / 2) * zoom + offsetY;
    return { top, left, zoom };
  }, [rotatedPoint, contentWidth, contentHeight, placeholderSize, canvasRotation]);

  useEffect(() => {
    const onViewportChange = () => {
      if (!placeholderContainerRef.current) {
        return;
      }
      const { top, left, zoom } = getPosition();
      placeholderContainerRef.current.style.transform = `translate3d(${left}px,${top}px,0) scale(${zoom})`;
      placeholderContainerRef.current.dataset.size = getSizeVariant(placeholderSize * zoom);
    };

    fabricCanvas.on(VIEWPORT_CHANGED_EVENT as any, onViewportChange);
    return () => {
      fabricCanvas.off(VIEWPORT_CHANGED_EVENT as any, onViewportChange);
    };
  }, [getPosition]);

  const position = getPosition();

  return {
    placeholderContainerRef,
    placeholderSize,
    position,
    isDragging,
    isMobile,
    initialSizeVariant: getSizeVariant(placeholderSize * position.zoom),
  };
}

export default usePlaceholderPositioning;
